/**
 * Up next: a press made while one thought is in flight, held until it lands.
 *
 * "One thought at a time" is the lock that stops a second pass being billed over
 * the first, and it stays exactly as it was. What changed is what happens to the
 * second press. It used to be dropped: a button toasted the refusal, a key did
 * nothing visible at all, and a usability study's persona who pressed T, I and B
 * saw only T run and never learned why. A press refused ONLY by the lock is the
 * reader's decision about what to do next, so it is taken as one — held here,
 * on screen under the live pass, until the pass in flight lands.
 *
 * THE GUARDRAIL. Each item is the reader's own press, and a press authorises
 * exactly one deferred pass. Nothing adds an item but a person pressing a verb
 * while the lock is held; the drain takes an item off before it dispatches it,
 * so it cannot run twice; nothing here chooses, repeats or substitutes a pass.
 * And a deferral is not a blank cheque. When its turn comes the item is judged
 * afresh (`upNextVerdict`): the claim still on the graph and still the kind of
 * thing the verb works on, no run going, the lock free, and the estimate no
 * higher than the one it was pressed against. Anything that moved sends it back
 * as a chip that asks again, or drops it with the sentence saying why — it never
 * runs on terms the press did not see. Two presses carry more terms than a verb
 * and a claim, and are held to those too: a rewrite to the words it would replace
 * and the count it said it would take with them, a rethink to the branch it named
 * and the check it read.
 *
 * Data, like a cue and a promote press (invariant 9): an item is a descriptor,
 * never a closure, so the whole queue survives a JSON round trip and nothing in
 * it can call a model. The dispatch is `ui/upNextPress.ts`, beside the other
 * presses, and it dispatches through the ordinary verbs — whose transport awaits
 * the durable save before any paid call (D84) — never around them.
 */

import type { ModelError } from '../engine/providers/contract'
import { fmtUsd, type Role } from './models'
import { evidenceRebaseQueue } from './rebase'
import { rewriteLoss, rewriteLossPhrase, type RewriteLoss } from './rewrite'
import { OP_HINT, OP_LABEL, type ReasonNode, type RunVerb, type Session } from './types'
import { quotedPress } from './vocabulary'

/** What a press can defer by its verb alone: the six moves, the source check (a
 *  survey, on the seed) and the panel — the keys D C X T I B V P and every door
 *  that carries them: the node card's buttons, the palette's rows, a proposal's
 *  chip and the coach's Take. A verb and a claim are the whole of such a press.
 *
 *  And three presses of the conjecture line, whose passes are a verb and a claim
 *  too (D286): "Propose insights" (U), "Search for prior art" and "Find
 *  what’s unsettled", from the card, the palette, the key and the coach. Two of
 *  that line's presses do not defer, and refuse under the lock in a sentence
 *  that names them (`EXECUTABLE_LOCK_REFUSAL`, `EXPLAIN_LOCK_REFUSAL`): the
 *  executable test, whose pass no dispatcher but its own press may reach (D283),
 *  and "Propose what explains this", which carries the sweep item it is aimed at.
 *
 *  Two more presses defer, and are not here because a verb and a claim are not
 *  the whole of them (`UpNextCarried`): the rewrite editor's "Re-derive from
 *  here", which carries the reader's draft and the count of claims it said it
 *  would replace, and "Rethink “<branch>”", which carries the branch it named.
 *  Each is entered by its own button (`pressRewrite`, `pressRethink`) or, held,
 *  by its own chip on the line — never by a key, a palette row or a proposal's
 *  chip, which have no draft or branch to give it.
 *
 *  What still does not defer. The free forms open a box rather than run a pass.
 *  The cross-examination: its key is another house's, its figure counts the
 *  branch it would read, and its "continue the unfinished reviews" is decided
 *  off the record at the press — three terms a deferred press could not be held
 *  to, so it keeps refusing under the lock. The promote presses (D42): a queued
 *  one would run after a pass had landed between the answer and its press. */
export type UpNextVerb = RunVerb | 'verify' | 'panel' | ConjectureLineVerb

/** The conjecture line's presses that wait on the line. See `UpNextVerb`. */
export type ConjectureLineVerb = 'conjecture' | 'priorArt' | 'sweep'

/** Each one's name, in its button's own words: the card, the palette's row, the
 *  line and every sentence about the press say it the same way. */
export const CONJECTURE_LINE_WORD: Record<ConjectureLineVerb, string> = {
  conjecture: 'Propose insights',
  priorArt: 'Search for prior art',
  sweep: 'Find what’s unsettled',
}

/** The two presses that carry their own terms onto the line — `OP_LABEL`'s
 *  Rewrite and Rethink. See `UpNextVerb`. */
export type UpNextCarried = 'revise' | 'rebase'

/** Everything the line can hold. */
export type UpNextKind = UpNextVerb | UpNextCarried

/**
 * A press from another door's vocabulary — a proposal's act, a cue's action —
 * as the line's verb, or null where that press does not defer (a form, the
 * cross-examination, a link scan). One narrowing, so the doors cannot come to
 * disagree about which of their presses may wait.
 */
export function asUpNextVerb(kind: string): UpNextVerb | null {
  if (kind === 'verify' || kind === 'panel') return kind
  if (Object.prototype.hasOwnProperty.call(CONJECTURE_LINE_WORD, kind))
    return kind as ConjectureLineVerb
  return Object.prototype.hasOwnProperty.call(OP_HINT, kind) ? (kind as RunVerb) : null
}

interface UpNextBase {
  id: string
  nodeId: string
  /** The claim's label when it was pressed — what the line names it by. */
  label: string
  /** The exploration it was pressed in. Nothing queues across explorations. */
  sessionId: string
  /** The estimate the press was made against, or null where the routed model
   *  has no list rate. A held item carries the figure it was held at. */
  est: number | null
  /**
   * Why it did not run when its turn came. Present, the item is a chip that
   * asks again and runs only on a new press; absent, it is waiting.
   */
  held?: string
}

/** A claim's two fields of wording, as a rewrite reads and writes them. */
export interface Wording {
  label: string
  claim: string
}

/** A move, a check or a panel: the verb and the claim are the whole press. */
export interface UpNextMove extends UpNextBase {
  verb: UpNextVerb
}

/**
 * A rewrite pressed under the lock. The editor closes on the press, so the draft
 * lives here until its turn — as strings, like everything on the line.
 */
export interface UpNextRewrite extends UpNextBase {
  verb: 'revise'
  /** The reader's words, as the editor held them at the press. */
  draft: Wording
  /** The claim's words at the press: the ones the draft replaces. A rewrite
   *  that lands first changes them, and this one would then replace words its
   *  press never saw. */
  wording: Wording
  /** What the press said it replaces — `rewriteLoss`, as its face printed it. */
  loss: RewriteLoss
}

/** A rethink pressed under the lock, naming the branch its button named. */
export interface UpNextRethink extends UpNextBase {
  verb: 'rebase'
  /** The branch the press named: the queue's next one-pass branch then. */
  branch: { id: string; label: string }
  /** The check the press read, by its date: a newer one is a different record. */
  checkedAt: number
}

export type UpNextItem = UpNextMove | UpNextRewrite | UpNextRethink

/** One or two presses, and no more. A longer list is a plan, and a plan is a
 *  run's job — with a budget, a stop and a report. */
export const UP_NEXT_MAX = 2

/** The lock's two sentences, as the verbs themselves say them: `runOp`'s for the
 *  six moves, the checks' for the rest. A refused key says what the button says. */
export const ONE_THOUGHT_BRANCH = 'One thought at a time — let the current branch finish.'
export const ONE_THOUGHT_PASS = 'One thought at a time — let the current pass finish.'

/**
 * The lock's sentences for the two conjecture-line presses that cannot wait on
 * the line, each naming its press — as the cross-examination's does
 * (`ACT_LOCK_REFUSAL`) — because a bare "one thought at a time" printed just
 * above the Up next line read as that line's denial (D162). Their passes raise
 * them, and their buttons are titled with them while the lock holds.
 */
export const EXECUTABLE_LOCK_REFUSAL =
  'One thought at a time — an executable test waits for the current pass to finish.'
export const EXPLAIN_LOCK_REFUSAL =
  'One thought at a time — a pass aimed at one sweep item waits for the current pass to finish.'
/** The free press that lands what a conjecture departs from takes a step of
 *  history, and one taken under a pass in flight would split that pass's own. */
export const DEPARTS_LOCK_REFUSAL =
  'One thought at a time — what it departs from goes on the graph once the current pass finishes.'

/**
 * A list of refusals to print under a row of presses, said once where two of
 * them are the lock's. A move is refused in the branch's words and a check in the
 * pass's (each is the sentence its own verb raises, so a chip and a key say the
 * same thing), and under a live run a row holding both printed two near-identical
 * lines one above the other. They are one fact — something holds the lock — so
 * the row keeps the branch's, the broader of the two.
 */
export function refusalsToPrint(refusals: readonly string[]): string[] {
  const distinct = [...new Set(refusals)]
  return distinct.includes(ONE_THOUGHT_BRANCH)
    ? distinct.filter((w) => w !== ONE_THOUGHT_PASS)
    : distinct
}
/** The checks' and the rethink's verbs refuse any pass in flight in the pass's
 *  words (`rederiveFromVerification` says the checks' sentence); the moves and
 *  the rewrite's re-derivation, which is a move, in the branch's. The conjecture
 *  line's three are the checks' kind: each refuses on `busy`, the worked
 *  example's unfurl included, rather than stopping it as a move does (D122). */
const passWorded = (verb: UpNextKind): boolean =>
  verb === 'verify' ||
  verb === 'panel' ||
  verb === 'rebase' ||
  Object.prototype.hasOwnProperty.call(CONJECTURE_LINE_WORD, verb)

export const lockRefusal = (verb: UpNextKind): string =>
  passWorded(verb) ? ONE_THOUGHT_PASS : ONE_THOUGHT_BRANCH

/** A press's name, in the words its key's price toast has always used. The
 *  rewrite and the rethink are `OP_LABEL`'s, the words their passes carry. */
export function upNextName(verb: UpNextKind, onSeed: boolean): string {
  if (verb === 'verify') return onSeed ? 'Survey the field' : 'Check against sources'
  if (verb === 'panel') return 'Three lenses'
  if (verb === 'conjecture' || verb === 'priorArt' || verb === 'sweep')
    return CONJECTURE_LINE_WORD[verb]
  return OP_LABEL[verb]
}

/**
 * The name a press carrying its own object goes by on the line and in every
 * sentence about it — "Rewrite “<label>”", "Rethink “<branch>”", the rethink's
 * own button's words — or null for a verb, which the line names beside its claim.
 */
export function upNextCalled(item: UpNextItem): string | null {
  if (item.verb === 'revise') return quotedPress(OP_LABEL.revise, item.label)
  if (item.verb === 'rebase') return quotedPress(OP_LABEL.rebase, item.branch.label)
  return null
}

/** Which `projectVerbUsd` arithmetic prices it. The rewrite and the rethink are
 *  each one move-shaped pass, and their buttons price them so (`VerbPrice`); so is
 *  a conjecture pass. The prior-art search and the sweep carry their own search
 *  allowances, larger than a check's, and are priced by them (D281). */
export const upNextPriceVerb = (
  verb: UpNextKind,
): 'move' | 'verify' | 'priorArt' | 'sweep' | 'panel' =>
  verb === 'verify' || verb === 'panel' || verb === 'priorArt' || verb === 'sweep' ? verb : 'move'

/** The role whose key the pass needs — a survey is the check's, like a check, and
 *  so are the prior-art search and the sweep, which search as a check does. */
export const upNextRole = (verb: UpNextKind): Role =>
  verb === 'verify' || verb === 'priorArt' || verb === 'sweep'
    ? 'check'
    : verb === 'panel'
      ? 'panel'
      : 'reason'

/**
 * Why this verb can never run on this claim, whatever the lock says — the verb's
 * own structural refusal, in its own sentence — or null. The seed has no panel,
 * and only a conjecture has a prior-art search: it reads what one departs from,
 * its leap and its prediction. Asked at the press and again at the item's turn,
 * because a claim can stop being a conjecture while its press waits (an undo).
 */
function structuralRefusal(verb: UpNextKind, node: ReasonNode): string | null {
  // `runPanel`'s own sentence: the seed has no panel, queued or not.
  if (verb === 'panel' && node.parentId === null)
    return 'A panel rules on a claim derived from the seed, not on the seed itself.'
  // `runPriorArt`'s own sentence.
  if (verb === 'priorArt' && (node.kind !== 'conjecture' || !node.conjecture))
    return 'A prior-art search reads what an insight departs from, its leap and its prediction, and this node has none of them.'
  return null
}

/** The slice of the store these read — structural, so a test needs no store. */
export interface UpNextView {
  busy: boolean
  interrupt: unknown
  run: { phase: string } | null
  session: Pick<Session, 'id' | 'nodes'>
  upNext: UpNextItem[]
}

const runGoing = (s: Pick<UpNextView, 'run'>): boolean => s.run !== null && s.run.phase !== 'ended'

/** The items of the exploration on screen — the only ones that exist to it. */
export const upNextHere = (s: Pick<UpNextView, 'session' | 'upNext'>): UpNextItem[] =>
  s.upNext.filter((item) => item.sessionId === s.session.id)

/**
 * Whether a press of this verb would meet the lock rather than run.
 *
 * The moves ask `lockHeld`'s question: the worked example's unfurl gives way to
 * them (D122), so a move pressed over it runs now and is never queued behind
 * it. The checks refuse ANY pass in flight, the unfurl included, so they queue
 * behind that too — and wait for it, because a queued item never stops one.
 * The rewrite's re-derivation is a move (`runOp` takes the lock for it), and the
 * rethink refuses any pass the way the checks do.
 */
export function upNextWaits(s: Pick<UpNextView, 'busy' | 'interrupt'>, verb: UpNextKind): boolean {
  if (passWorded(verb)) return s.busy
  return s.busy && s.interrupt === null
}

/** Whether a press made now could be queued at all — the buttons read this to
 *  stay offered under the lock. */
export function upNextRoom(s: Pick<UpNextView, 'run' | 'session' | 'upNext'>): boolean {
  return !runGoing(s) && upNextHere(s).length < UP_NEXT_MAX
}

/**
 * Whether the lock shuts a press of this verb out altogether: it would wait, and
 * the line has no room for it (a live run, or two already up next). What a check
 * button, the panel's button and the survey read to grey out, and what a palette
 * row reads to stand down — the verbs' own `locked && !room`, asked per verb,
 * because the checks wait behind the worked example's unfurl and the moves stop
 * it (D122). A press this does not shut out still goes through `pressVerb`,
 * which says out loud why when it cannot be taken (a duplicate, no key).
 */
export function upNextShut(
  s: Pick<UpNextView, 'busy' | 'interrupt' | 'run' | 'session' | 'upNext'>,
  verb: UpNextKind,
): boolean {
  return upNextWaits(s, verb) && !upNextRoom(s)
}

/**
 * Why a press that met the lock cannot be queued, in the sentence the reader
 * gets — or null when it can.
 *
 * `callable` is whether the verb's role has a key. Without one the press was
 * never going to run, and the honest answer is the one it has always had under
 * the lock; the key's own sentence belongs to a press made when the lock is
 * free, which is where the verb says it.
 */
export function upNextRefusal(
  s: UpNextView,
  verb: UpNextKind,
  nodeId: string,
  callable: boolean,
): string | null {
  const node = s.session.nodes[nodeId]
  if (!node) return 'That node is no longer on the graph.'
  const never = structuralRefusal(verb, node)
  if (never) return never
  // A live run owns what comes next — its pod, its gate and its Stop. Nothing
  // queues behind it, and nothing queued ever runs inside one.
  if (runGoing(s) || !callable) return lockRefusal(verb)
  const here = upNextHere(s)
  const name = upNextName(verb, node.parentId === null)
  // One press, one pass: a second rewrite of the same claim is refused like a
  // second Invert, and the first one's ✕ is the way to write it again.
  const waiting = here.find((item) => !item.held && item.verb === verb && item.nodeId === nodeId)
  if (waiting) return `${upNextCalled(waiting) ?? `${name} on “${node.label}”`} is already up next.`
  // A held chip for this same press is replaced by it, so it takes no room.
  const taken = here.filter((item) => !(item.held && item.verb === verb && item.nodeId === nodeId))
  if (taken.length >= UP_NEXT_MAX)
    return 'Up next is full — take one off with its ✕ to queue this instead.'
  return null
}

/**
 * Whether the pass in front failed in a way the next press would meet too, in
 * the sentence its chip gives — or null where the next may go.
 *
 * Wider than `endsTheRun`, and on purpose. A run stops only on a wall that will
 * never come down by itself (the key, the credits, the save), because a run
 * carries on alone and paces itself past the rest. A held press costs one more
 * press, and a queued pass fired straight into a rate limit the transport had
 * just waited out — measured: the Invert behind a rate-limited Deepen went out
 * half a second after it — fails the same way and takes the press with it. So
 * the line also holds on the API's side and the wire's, where the next request a
 * second later would meet what the last one did. A failure of the pass's own
 * request (`request`) or an unknown one says nothing about the next, and a Stop
 * clears the line before it can matter.
 *
 * Exhaustive over the kind with no `default`: a new kind stops the build here
 * until somebody decides what the line does behind it.
 */
export function wallAhead(kind: ModelError['kind'] | undefined): string | null {
  const held = (what: string) => `The pass in front of it ${what}. Press it to try again.`
  switch (kind) {
    case 'auth':
      return held('was refused for its key, and this one would be too')
    case 'quota':
      return held('found the account out of credits, and this one would too')
    case 'storage':
      return held('could not be saved, and this one would wait on the same save')
    case 'rate':
      return held('was rate limited, and this one would meet the same limit now')
    case 'server':
      return held('failed on the API’s side, and this one would likely fail the same way now')
    case 'network':
      return held('lost its connection to the API, and this one would likely lose it too')
    case 'timeout':
      return held('hung without an answer for ten minutes')
    case 'request':
    case 'aborted':
    case 'unknown':
    case undefined:
      return null
  }
}

export type UpNextVerdict =
  | { kind: 'run' }
  /** Not its turn: something holds the lock. Asked again when it falls. */
  | { kind: 'wait' }
  /** Back to the reader as a chip that asks again, with the figure now. */
  | { kind: 'hold'; reason: string; est: number | null }
  /** It cannot run at all any more; the reason is said once. */
  | { kind: 'drop'; reason: string }

/** The sentence for a press whose claim left the graph before its turn — a
 *  prune, an undo — said by the drain and by a chip pressed over it alike. */
export const upNextGone = (item: UpNextItem): string =>
  `${upNextCalled(item) ?? `${upNextName(item.verb, false)} on “${item.label}”`} did not run — that claim is no longer on the graph.`

/** Whether a rewrite would now take more off the board than its press said:
 *  more claims, more connection candidates, or a panel reading it did not
 *  count. Less is not a change against the press — it promised at most. */
export function lossGrew(then: RewriteLoss, now: RewriteLoss): boolean {
  return (
    now.claims > then.claims || now.connections > then.connections || (now.panel && !then.panel)
  )
}

/** The branch a rethink of this claim would take now — `rederiveFromVerification`'s
 *  own pick, the queue's first one-pass branch — or null where none is left. */
export function nextRethink(
  session: Session,
  nodeId: string,
): { id: string; label: string } | null {
  const next = evidenceRebaseQueue(session, nodeId).find((b) => b.action === 'rederive')
  return next ? { id: next.root.id, label: next.root.label } : null
}

/**
 * A carried press's own terms, judged at its turn: whether the rewrite would
 * still replace the words and at most the claims its press saw, and whether the
 * rethink would still take the branch its press named, off the check it read.
 * Null when they hold. Asked after the rules every press is held to, so a chip
 * says the one thing that moved against this press in particular.
 */
function carriedTermsMoved(
  session: Session,
  item: UpNextRewrite | UpNextRethink,
): { kind: 'hold' | 'drop'; reason: string } | null {
  const node = session.nodes[item.nodeId]!
  if (item.verb === 'revise') {
    if (node.label !== item.wording.label || node.claim !== item.wording.claim)
      return {
        kind: 'hold',
        reason: `“${item.label}” was rewritten before its turn, so your wording would replace words you did not see. Press it to rewrite it as it reads now.`,
      }
    const now = rewriteLoss(session, item.nodeId)
    if (lossGrew(item.loss, now))
      return {
        kind: 'hold',
        // What it would replace now is said beside this by the chip, live
        // (`UpNextLine`), since that is the count the chip's press is held to.
        reason: `More landed under “${item.label}” before its turn than its press counted (${rewriteLossPhrase(item.loss) ?? 'only the wording'}). Press it to rewrite anyway.`,
      }
    return null
  }
  const next = nextRethink(session, item.nodeId)
  if (!next)
    return {
      kind: 'drop',
      reason: `${upNextCalled(item)} did not run — nothing under “${node.label}” is left for one pass to rethink.`,
    }
  if (node.verification?.checkedAt !== item.checkedAt)
    return {
      kind: 'hold',
      reason: `“${node.label}” was checked again before its turn, so a rethink would read a newer record than the one you pressed on. Press it to rethink against that one.`,
    }
  if (next.id !== item.branch.id)
    return {
      kind: 'hold',
      reason: `The next branch to rethink under “${node.label}” is now “${next.label}”, not “${item.branch.label}”. Press it to rethink that one.`,
    }
  return null
}

/** Whether the figure moved against the press. An unknown figure where there
 *  was one, or one where there was none, is a change the press did not see. */
export function estimateRose(then: number | null, now: number | null): boolean {
  if (then === null || now === null) return then !== now
  return now > then + 1e-9
}

/**
 * The item's turn, judged at the moment it comes — never at the moment it was
 * pressed. `now` is what the press path would compute afresh: the estimate at
 * today's routing, and whether the verb's role has a key. `failedBefore` is the
 * kind of failure the pass in front of it ended on, if it failed — read by
 * `wallAhead`, over the error's TYPE (invariant 7's rule). A rewrite and a
 * rethink are then held to their own terms (`carriedTermsMoved`), read here off
 * the whole document, since what they would replace or take is a walk of it.
 */
export function upNextVerdict(
  s: Pick<UpNextView, 'busy' | 'run'> & { session: Session },
  item: UpNextItem,
  now: { est: number | null; callable: boolean; failedBefore?: ModelError['kind'] },
): UpNextVerdict {
  const name =
    upNextCalled(item) ?? upNextName(item.verb, s.session.nodes[item.nodeId]?.parentId === null)
  if (item.sessionId !== s.session.id)
    return {
      kind: 'drop',
      reason: `${name} was pressed in another exploration, so it did not run.`,
    }
  // Strictly `busy`, not `lockHeld`: the worked example's unfurl gives way to a
  // PRESS, and this is not one — a queued item waits for it instead.
  if (s.busy) return { kind: 'wait' }
  const node = s.session.nodes[item.nodeId]
  if (!node) return { kind: 'drop', reason: upNextGone(item) }
  const never = structuralRefusal(item.verb, node)
  if (never) return { kind: 'drop', reason: never }
  if (runGoing(s))
    return {
      kind: 'hold',
      reason: 'A run started before its turn. Press it to run it now.',
      est: now.est,
    }
  if (!now.callable)
    return {
      kind: 'hold',
      reason: 'The key this pass needs is no longer here. Press it to be asked for one.',
      est: now.est,
    }
  // The pass in front met a wall this one would meet too: a line of presses must
  // not walk into it one request at a time.
  const wall = wallAhead(now.failedBefore)
  if (wall) return { kind: 'hold', reason: wall, est: now.est }
  if (item.verb === 'revise' || item.verb === 'rebase') {
    const moved = carriedTermsMoved(s.session, item)
    if (moved?.kind === 'drop') return { kind: 'drop', reason: moved.reason }
    if (moved) return { kind: 'hold', reason: moved.reason, est: now.est }
  }
  if (estimateRose(item.est, now.est))
    return {
      kind: 'hold',
      reason:
        now.est === null
          ? 'The model it would run on has no list rate now, so there is no figure to hold it to.'
          : item.est === null
            ? `It now carries an estimate, up to about ${fmtUsd(now.est)}, that it was not pressed against.`
            : `The estimate rose from ${fmtUsd(item.est)} to ${fmtUsd(now.est)} since it was pressed.`,
      est: now.est,
    }
  return { kind: 'run' }
}
